import { KatalComponent } from '../../shared/base';

const Direction = {
  Forward: 1,
  Backward: -1,
};

/**
 * @component {kat-radiobutton} KatalRadiobutton Allows customers to choose only one of a predefined set of options. Only a single radio button may be selected in a list of options.
 * @guideline Do Group radio buttons by the name attribute.  Radio buttons with the same name will function together as a group
 * @guideline Do Accessibility requirements for Radio buttons require they should be inside an element with role="radiogroup".
 * @event change Fires when a radio button has been selected. The detail contains a checked property with the current state
 * @event focus Fires when the user focuses into the radio button.
 * @event blur Fires when the user stops focusing the radio button.
 * @example Basic {"label": "Option1", "value":"value1", "checked":"true", "constraint-label":""}
 * @example WithConstraint {"label": "Option1", "value":"value1", "constraint-label":"This is the first option"}
 * @example Disabled {"label": "Option1", "value":"value1", "disabled":"true", "constraint-label":""}
 * @status Production
 * @theme flo
 */
class KatalRadiobutton extends KatalComponent {
  constructor() {
    super(KatalRadiobutton.observedAttributes);

    const uniqueIdLength = 10;
    this._uniqueId = KatalComponent.generateRandomId(uniqueIdLength);
    this._constraintId = KatalComponent.generateRandomId(uniqueIdLength);
  }

  static get observedAttributes() {
    return [
      'value',
      'label',
      'constraint-label',
      'checked',
      'unique-id',
      'name',
      'disabled',
      'kat-aria-label',
      'kat-aria-labelledby',
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', evt => {
      if (evt.keyCode === 13 || evt.keyCode === 32) {
        evt.stopImmediatePropagation();
        if (!this.disabled) {
          this.checked = true;
        }
      }

      // Go to the previous radio if up arrow or right arrow
      if (evt.keyCode === 38 || evt.keyCode === 37) {
        this.selectNextValidSibling(Direction.Backward);
        // Go to the next radio if down arrow or left arrow
      } else if (evt.keyCode === 40 || evt.keyCode === 39) {
        this.selectNextValidSibling(Direction.Forward);
      }
    });

    this.render();
  }

  generateRadioButton() {
    let radioButton = this.querySelector('input');

    if (radioButton === null) {
      this.innerHTML = `
                <div>
                    <input type="radio" id="${this.uniqueId}"/>
                    <span class="kat-radiobutton-icon" role="radio" aria-details="${this._constraintId}"></span>
                </div>
                <div class="labels" aria-hidden="true">
                    <kat-label class="text" for="${this.uniqueId}"></kat-label>
                    <kat-label id="${this._constraintId}" class="constraint" for="${this.uniqueId}" variant="constraint"></kat-label>
                </div>
            `;
      radioButton = this.querySelector('input');

      const icon = this.querySelector('.kat-radiobutton-icon');
      icon.addEventListener('focus', this.handleFocus.bind(this));
      icon.addEventListener('blur', this.handleBlur.bind(this));
      icon.addEventListener('click', this.handleClick.bind(this));

      const text = this.querySelector('.labels .text');
      text.addEventListener('click', this.handleClick.bind(this));

      const constraint = this.querySelector('.labels .constraint');
      constraint.addEventListener('click', this.handleClick.bind(this));
    }

    return radioButton;
  }

  /**
   * @param {string} value The value of the radio button
   * @required
   */
  get value() {
    return this.getAttributeOrDefault('value', '');
  }
  set value(value) {
    this.setAttribute('value', value);
  }

  /**
   * @param {string} label The label displayed to the user for
   */
  get label() {
    return this.getAttributeOrDefault('label', '');
  }
  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @param {string} constraint-label Provides users with more information about what they enter into the input
   */
  get constraintLabel() {
    return this.getAttributeOrDefault('constraint-label', '');
  }
  set constraintLabel(value) {
    this.setAttribute('constraint-label', value);
  }

  /**
   * @param {boolean} checked Determines of the radio button is selected or not
   */
  get checked() {
    return this.getBooleanAttribute('checked');
  }
  set checked(value) {
    const before = this.checked;
    this.setBooleanAttribute('checked', value);

    const icon = this.querySelector('.kat-radiobutton-icon');
    if (this.checked) {
      icon.setAttribute('aria-checked', 'true');
    } else {
      icon.setAttribute('aria-checked', 'false');
    }

    if (before !== this.checked && this.checked) {
      this.toggleSiblings();
      this.dispatchCustomEvent('change', {
        checked: value,
        value: this.value,
      });
    }
  }

  /**
   * @param {string} unique-id The id of the form element
   */
  get uniqueId() {
    let id = this.getAttribute('unique-id');
    if (id == null) {
      id = this._uniqueId;
    }
    return id;
  }
  set uniqueId(value) {
    this.setAttribute('unique-id', value);
  }

  /**
   * @param {string} name The form name of the element
   */
  get name() {
    return this.getAttributeOrDefault('name', '');
  }
  set name(value) {
    this.setAttribute('name', value);
  }

  /**
   * @param {string} kat-aria-label The label used for screen readers.
   */
  get katAriaLabel() {
    return this.getAttributeOrDefault('kat-aria-label', '');
  }
  set katAriaLabel(value) {
    this.setAttribute('kat-aria-label', value);
  }

  /**
   * @param {string} kat-aria-labelledby The ID of an element used as a label for screen readers.
   */
  get katAriaLabelledby() {
    return this.getAttributeOrDefault('kat-aria-labelledby', '');
  }
  set katAriaLabelledby(value) {
    this.setAttribute('kat-aria-labelledby', value);
  }

  /**
   * @param {boolean} disabled prevents the user from changing the value of an input and prevents events from being triggered
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }
  set disabled(value) {
    this.setBooleanAttribute('disabled', value);
    const icon = this.querySelector('.kat-radiobutton-icon');
    if (this.disabled) {
      icon.setAttribute('aria-disabled', 'true');
    } else {
      icon.setAttribute('aria-disabled', 'false');
    }
  }

  handleFocus() {
    this.dispatchCustomEvent('focus', {}, true);
  }

  handleBlur() {
    this.dispatchCustomEvent('blur', {}, true);
  }

  handleClick(evt) {
    evt.stopImmediatePropagation();
    if (!this.disabled) {
      this.checked = true;
      this.querySelector('.kat-radiobutton-icon').focus();
    }
  }

  /**
   * Call to programmatically focus the radio button.
   * @katalmethod
   */
  focus() {
    if (!this.disabled) {
      this.querySelector('.kat-radiobutton-icon').focus();
    }
  }

  /**
   * Call to programmatically blur the radio button.
   * @katalmethod
   */
  blur() {
    this.querySelector('.kat-radiobutton-icon').blur();
  }

  /**
   * Call to programmatically click the radio button.
   * @katalmethod
   */
  click() {
    this.querySelector('.kat-radiobutton-icon').click();
  }

  render() {
    const checked = this.checked;
    const disabled = this.disabled;
    const uniqueId = this.uniqueId;
    const value = this.value;
    const name = this.name;

    const radioButton = this.generateRadioButton();
    radioButton.setAttribute('id', uniqueId);
    radioButton.setAttribute('value', value);
    radioButton.setAttribute('name', name);
    radioButton.checked = checked;

    const icon = this.querySelector('.kat-radiobutton-icon');
    icon.setAttribute(
      'aria-label',
      this.katAriaLabel ? this.katAriaLabel : this.label
    );
    if (this.katAriaLabelledby) {
      icon.setAttribute('aria-labelledby', this.katAriaLabelledby);
    } else {
      icon.removeAttribute('aria-labelledby');
    }

    const labels = this.querySelector('.labels');
    if (disabled) {
      radioButton.setAttribute('disabled', '');
      labels.classList.add('disabled');
    } else {
      radioButton.removeAttribute('disabled');
      labels.classList.remove('disabled');
    }

    const textLabel = this.querySelector('kat-label[class="text"]');
    textLabel.setAttribute('for', uniqueId);
    textLabel.setAttribute('text', this.label);

    const constraintLabel = this.querySelector('kat-label[class="constraint"]');
    constraintLabel.setAttribute('for', uniqueId);
    constraintLabel.setAttribute('text', this.constraintLabel);

    this.updateTabSetting();
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue) {
    this.render();

    if (attr === 'name' && oldValue) {
      // If name was changed tell the old group to update
      this.getRadioInGroup(oldValue).forEach(radio => {
        // Basically checks if the element is a connected web component
        if (radio.updateTabSetting) {
          radio.updateTabSetting();
        }
      });
    }

    // Update all siblings if my attributes that affect tab behaviour changed
    if (attr === 'name' || attr === 'checked' || attr === 'disabled') {
      this.getRadioInGroup()
        // Basically checks if the element is a connected web component
        .filter(radio => radio !== this && radio.updateTabSetting)
        .forEach(radio => radio.updateTabSetting());
    }
  }

  getRadioInGroup(altName) {
    const name = altName ? altName : this.name;
    return [].slice.call(
      document.querySelectorAll(`kat-radiobutton[name='${name}']`)
    );
  }

  toggleSiblings() {
    this.getRadioInGroup()
      .filter(radio => radio !== this)
      .forEach(sibling => {
        sibling.checked = false;
      });
  }

  selectNextValidSibling(direction) {
    const radios = this.getRadioInGroup();

    const myIndex = radios.indexOf(this);

    let targetIndex = myIndex;
    do {
      targetIndex += direction;
      if (targetIndex < 0) {
        targetIndex = radios.length - 1;
      } else if (targetIndex >= radios.length) {
        targetIndex = 0;
      }

      if (!radios[targetIndex].disabled) {
        radios[targetIndex].click();
        break;
      }
    } while (myIndex !== targetIndex);
  }

  updateTabSetting() {
    const radios = this.getRadioInGroup();
    const anyChecked = radios.some(radio => radio.checked && !radio.disabled);
    // First radio button that isn't disabled
    let first = null;
    for (let r = 0; r < radios.length; r++) {
      if (!radios[r].disabled) {
        first = radios[r];
        break;
      }
    }

    // This complicated logic here is designed to allow only non-disabled radio buttons to be tabbed to. Also native behaviour is to have
    // the first radio button in the group be the tab target if none are checked or the checked one be the tab target.
    // By setting tabindex to "0" we state that it can be tabbed to, by setting it to -1 we state that it can't be tabbed to at all.
    const icon = this.querySelector('.kat-radiobutton-icon');
    if ((!this.disabled && this.checked) || (first === this && !anyChecked)) {
      icon.setAttribute('tabindex', '0');
    } else {
      icon.removeAttribute('tabindex');
    }
  }
}

KatalComponent.register('kat-radiobutton', KatalRadiobutton);
